import React, {FC, lazy, memo, ReactNode, useEffect, useState} from 'react';

import {AppFrame} from '../../../../src/components/app/appFrame';
import {wrapWithSuspense} from '../../../../src/helpers/suspenseWrapperHelpers';
import {KnowledgeBasePanelId} from '../../../../src/store/panels/panelStateBase';
import {
  RenderProfiler,
  RenderTypesEnum,
} from '../../../../srcCommon/services/performanceMetrics/renderProfiler';
import {Layer} from '../../../shared/ui/src/components/layers/layer';

/*
 * Props.
 */

interface KnowledgeBaseFrameProps {
  isVisible: boolean;
  panelId: KnowledgeBasePanelId;
  children: ReactNode;
}

/*
 * Component.
 */

const PageRoutingContainer = lazy(() =>
  // @ts-expect-error Bypass the TS error around wrong module type since the `import()` directive is handled by Webpack
  import(/* webpackChunkName: 'knowledgeBase', webpackPrefetch: true */ './pages/pageRoutingContainer').then(
    (module) => ({
      default: module.PageRoutingContainer,
    }),
  ),
);

export const KnowledgeBaseFrame: FC<KnowledgeBaseFrameProps> = memo((props) => {
  const {isVisible, panelId, children} = props;
  const [hasVisited, setHasVisited] = useState(false);

  // Only render once the user has visited the route once.
  useEffect(() => {
    if (isVisible && !hasVisited) {
      setHasVisited(true);
    }
  }, [hasVisited, isVisible]);

  return (
    <>
      <Layer>{children}</Layer>
      <Layer>
        {/* If the user navigates away (isVisible is false), we want to keep the panel but unmount the DOM nodes. */}
        {hasVisited && (
          <AppFrame isVisible={isVisible}>
            <RenderProfiler type={RenderTypesEnum.KNOWLEDGE_BASE}>
              {isVisible && wrapWithSuspense(<PageRoutingContainer panelId={panelId} />)}
            </RenderProfiler>
          </AppFrame>
        )}
      </Layer>
    </>
  );
});
